import React from 'react';
import { Group } from '@mantine/core';
import PlatformBackButton from './PlatformBackButton';

/**
 * Consistent top-left navigation bar shown inside every platform.
 *
 * Renders, left-to-right:
 *   1. "Back to Platform Selector" — exits the platform entirely (the broadest
 *      action; kept leftmost to preserve the anchor position the SPA already used).
 *   2. (optional) "<Platform> home" — returns to the platform's own landing/hub.
 *      Rendered only when `showHome` is true and `onBackToHome` is supplied, so a
 *      page that already IS the hub doesn't show a redundant button.
 *
 * Presentational only — the caller wires the handlers. Router-based platforms pass
 * `onBackToHome={() => navigate('/')}` with `showHome={location.pathname !== '/'}`;
 * hub-based platforms (state, not URL) pass `onBackToHome={() => setSelectedTool(null)}`
 * with `showHome` always true while a tool is open.
 *
 * Both buttons reuse {@link PlatformBackButton} so styling stays identical across
 * the app (see that file for the onGradient/color conventions).
 *
 * Props:
 *   - onBackToPlatformSelector (required): exit-the-platform handler.
 *   - onBackToHome (optional): return-to-platform-hub handler.
 *   - showHome (default false): whether to render the home button.
 *   - homeLabel (default "Platform home"): label for the home button.
 *   - color: Mantine color for the non-gradient mode (e.g. "orange").
 *   - onGradient (default false): legible-on-hero mode (see PlatformBackButton).
 *   - mb: bottom margin passthrough (defaults to "md").
 */
export default function PlatformNavBar({
  onBackToPlatformSelector,
  onBackToHome,
  showHome = false,
  homeLabel = 'Platform home',
  color,
  onGradient = false,
  mb = 'md'
}) {
  return (
    <Group gap="xs" mb={mb}>
      <PlatformBackButton
        onClick={onBackToPlatformSelector}
        color={color}
        onGradient={onGradient}
        mb={0}
      />
      {showHome && onBackToHome && (
        <PlatformBackButton
          onClick={onBackToHome}
          color={color}
          onGradient={onGradient}
          label={homeLabel}
          mb={0}
        />
      )}
    </Group>
  );
}
